import { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import { useTheme } from "../ThemeProvider";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import { emojiVisualProcedencia } from "../../utils/procedenciaColor";
import {
  parsePrecioVentaEntero,
  parseEuroAmount,
  parsePercentFlexible,
  formatPrecioVentaEntero,
  formatEuroAmount,
  roundEuro2,
} from "../../utils/moneyParse";
import { esOperacionAlquiler } from "../../utils/esOperacionAlquiler";
import BloqueLlavesUbicacion from "./BloqueLlavesUbicacion";

function fmtHistoricoFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtHistoricoEuro(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n).toLocaleString("es-ES")} €`;
}

function TendenciaPrecio({ actual, anterior }) {
  const a = Number(actual);
  const b = Number(anterior);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === b) return null;
  const sube = a > b;
  return (
    <span
      style={{
        marginLeft: 5,
        color: sube ? "#16a34a" : "#dc2626",
        fontSize: 12,
        lineHeight: 1,
      }}
      title={sube ? "Sube respecto al registro anterior" : "Baja respecto al registro anterior"}
      aria-hidden
    >
      {sube ? "⬆️" : "⬇️"}
    </span>
  );
}

function BloqueHistoricoPrecios({ propiedadId, historicoRefreshKey, c, isDark, puedeVer = true, esAlquiler = false, tv = (x) => x }) {
  if (!puedeVer) return null;
  return (
    <BloqueHistoricoPreciosInner
      propiedadId={propiedadId}
      historicoRefreshKey={historicoRefreshKey}
      c={c}
      isDark={isDark}
      esAlquiler={esAlquiler}
      tv={tv}
    />
  );
}

function BloqueHistoricoPreciosInner({ propiedadId, historicoRefreshKey, c, isDark, esAlquiler = false, tv = (x) => x }) {
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!propiedadId) {
      setFilas([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/propiedades/${propiedadId}/historico-precios`)
      .then((r) => {
        if (!cancelled) setFilas(r.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFilas([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propiedadId, historicoRefreshKey]);

  if (!propiedadId) return null;

  const thStyle = {
    padding: "6px 10px",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: c.textMuted,
    textAlign: "left",
    borderBottom: `1px solid ${c.border}`,
    whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "7px 10px",
    fontSize: 12,
    color: c.textPrimary,
    borderBottom: `1px solid ${c.border}`,
    verticalAlign: "middle",
  };

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          borderTop: `1px dashed ${c.sectionBorder}`,
          marginBottom: 12,
          paddingTop: 4,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#4f46e5",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {tv("Histórico Precios")}
        </span>
      </div>
      {loading ? (
        <p style={{ fontSize: 12, color: c.textMuted, margin: 0 }}>{tv("Cargando histórico…")}</p>
      ) : (
        <div
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            overflow: "auto",
            maxHeight: filas.length > 6 ? 220 : "none",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr style={{ background: isDark ? "#0e1117" : "#f8fafc" }}>
                <th style={thStyle}>{tv("Fecha y hora")}</th>
                <th style={thStyle}>{esAlquiler ? tv("Precio alquiler") : tv("Precio venta")}</th>
                <th style={thStyle}>{tv("Precio propietario")}</th>
                <th style={thStyle}>{esAlquiler ? tv("Honorarios") : tv("Total honorarios")}</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((row, i) => {
                const prev = i > 0 ? filas[i - 1] : null;
                const etiquetaCreacion = row.es_creacion && i === 0 ? ` (${tv("alta")})` : "";
                return (
                  <tr key={row.id || `h-${i}`}>
                    <td style={tdStyle}>
                      {fmtHistoricoFecha(row.created_at)}
                      {etiquetaCreacion ? (
                        <span style={{ fontSize: 10, color: c.textMuted }}>{etiquetaCreacion}</span>
                      ) : null}
                    </td>
                    <td style={tdStyle}>
                      {fmtHistoricoEuro(row.precio_venta)}
                      {prev ? (
                        <TendenciaPrecio actual={row.precio_venta} anterior={prev.precio_venta} />
                      ) : null}
                    </td>
                    <td style={tdStyle}>
                      {fmtHistoricoEuro(row.precio_propietario)}
                      {prev ? (
                        <TendenciaPrecio
                          actual={row.precio_propietario}
                          anterior={prev.precio_propietario}
                        />
                      ) : null}
                    </td>
                    <td style={tdStyle}>
                      {fmtHistoricoEuro(row.total_honorarios)}
                      {prev ? (
                        <TendenciaPrecio
                          actual={row.total_honorarios}
                          anterior={prev.total_honorarios}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CALIFICACIONES = [
  { v: 0, label: "0★ Chollo" },
  { v: 1, label: "1★ Adecuado" },
  { v: 2, label: "2★ Caro" },
  { v: 3, label: "3★ Fuera de mercado" },
];

const PRECIO_NEGOCIABLE_OPTS = [
  { v: "si", label: "Sí" },
  { v: "no", label: "No" },
  { v: "no_sabe", label: "No sabe" },
];

const CARTEL_OPCIONES = [
  "Vitalcasa",
  "Propietario",
  "Otra agencia",
  "Varias agencias",
  "Otro",
];

function etiquetaPrecioNegociable(valor) {
  return PRECIO_NEGOCIABLE_OPTS.find((o) => o.v === valor)?.label || null;
}

/** Actualiza o quita la línea «Precio negociable: …» (+ Obs) en Info visita. */
function infoVisitaConPrecioNegociable(infoVisita, valorPrecio, obs) {
  const lineas = String(infoVisita || "")
    .split("\n")
    .map((l) => l.trimEnd())
    .filter((l) => l.trim() && !/^Precio negociable:\s/i.test(l.trim()));
  const etiqueta = etiquetaPrecioNegociable(valorPrecio);
  if (etiqueta) {
    const obsTxt = String(obs || "").trim();
    lineas.push(obsTxt ? `Precio negociable: ${etiqueta} · ${obsTxt}` : `Precio negociable: ${etiqueta}`);
  }
  return lineas.join("\n");
}

function fmtCampoPrecio(v, decimales) {
  const n = decimales ? parseEuroAmount(v) : parsePrecioVentaEntero(v);
  if (!Number.isFinite(n)) return "";
  return decimales ? formatEuroAmount(n) : formatPrecioVentaEntero(n);
}

function CampoPrecio({
  label,
  name,
  form,
  cambiarDirecto,
  readOnly,
  highlight,
  unidad = "€",
  err,
  decimales = false,
  inputId,
  c,
  isDark,
  labelStyle,
  inp,
  disabled = false,
  tv = (x) => x,
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");

  const formatted = fmtCampoPrecio(form[name], decimales);

  return (
    <div>
      <label style={{ ...labelStyle, color: err ? c.accentRed : c.textMuted }}>
        {tv(label)}{err && <span style={{ color: c.accentRed }}> *</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {readOnly ? (
          <input
            type="text"
            value={formatted}
            readOnly
            style={inp(false, true, highlight)}
          />
        ) : (
          <input
            id={inputId}
            type="text"
            inputMode="decimal"
            name={name}
            value={focused ? draft : formatted}
            disabled={disabled}
            title={disabled ? "No tienes permiso para modificar" : undefined}
            onFocus={() => {
              setFocused(true);
              const n = decimales ? parseEuroAmount(form[name]) : parsePrecioVentaEntero(form[name]);
              setDraft(Number.isFinite(n) ? String(decimales ? n : Math.round(n)) : "");
            }}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setFocused(false);
              const n = decimales ? parseEuroAmount(draft) : parsePrecioVentaEntero(draft);
              cambiarDirecto(
                name,
                Number.isFinite(n) ? (decimales ? roundEuro2(n) : Math.round(n)) : "",
              );
            }}
            style={{ ...inp(err, false, highlight), opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
          />
        )}
        <span style={{ fontSize: 13, color: c.textMuted, fontWeight: 600 }}>{unidad}</span>
      </div>
    </div>
  );
}

function BloquePrecios({
  form,
  cambiar,
  cambiarDirecto,
  camposError,
  c,
  isDark,
  puedeVerPrecioVenta = true,
  puedeModificarPrecioVenta = true,
  puedeVerPrecioPropietario = true,
  puedeModificarPrecioPropietario = true,
  puedeVerHonorariosPct = true,
  puedeModificarHonorariosPct = true,
  puedeVerHonorariosEur = true,
  puedeModificarHonorariosEur = true,
  puedeVerHonorariosCalc = true,
  puedeVerIva = true,
  puedeModificarIva = true,
  esAlquiler = false,
  tv = (x) => x,
}) {
  const hasError = (name) => camposError?.includes(name);
  const ivaPct = parseFloat(form.iva_porcentaje) || 21;

  const calcDesdeVentaYProp = () => {
    const pv = parsePrecioVentaEntero(form.precio_venta);
    // Ampliar: propietario puede llevar céntimos (modelos 4%/5%)
    const pp = parseEuroAmount(form.precio_propietario);
    if (!Number.isFinite(pv) || !Number.isFinite(pp) || pv <= pp) return;
    const dif = pv - pp;
    const honBase = dif / (1 + ivaPct / 100);
    const iva = dif - honBase;
    cambiarDirecto("honorarios_euros", roundEuro2(honBase));
    cambiarDirecto("iva_euros", roundEuro2(iva));
    cambiarDirecto("honorarios_porcentaje", roundEuro2((honBase / pv) * 100));
    cambiarDirecto("total_honorarios", roundEuro2(dif));
  };

  const calcDesdeVentaYPct = () => {
    const pv = parsePrecioVentaEntero(form.precio_venta);
    const pct = parsePercentFlexible(form.honorarios_porcentaje);
    if (!Number.isFinite(pv) || !Number.isFinite(pct)) return;
    const honBase = pv * (pct / 100);
    const iva = honBase * (ivaPct / 100);
    const total = honBase + iva;
    cambiarDirecto("honorarios_euros", roundEuro2(honBase));
    cambiarDirecto("iva_euros", roundEuro2(iva));
    // Incluidos %: propietario = venta − (hon + IVA), con céntimos (225k → 211.387,50)
    cambiarDirecto("precio_propietario", roundEuro2(pv - total));
    cambiarDirecto("total_honorarios", roundEuro2(total));
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 5, display: "block",
  };
  const inp = (err, readOnly, highlight) => ({
    padding: "8px 12px",
    border: `1.5px solid ${err ? c.accentRed : c.border}`,
    borderRadius: 8, fontSize: 14,
    fontWeight: highlight ? 700 : 400,
    color: readOnly ? c.textSecondary : c.textPrimary,
    background: err
      ? (isDark ? "#2d1515" : "#fff5f5")
      : highlight
        ? (isDark ? "#1a2a10" : "#f0fdf4")
        : readOnly
          ? (isDark ? "#0e1117" : "#f8fafc")
          : c.bgInput,
    width: "100%", outline: "none",
  });

  return (
    <div id="bloque-precios-venta" style={{ scrollMarginTop: 120 }}>
      {/* Divisor */}
      <div style={{
        borderTop: `1px dashed ${c.sectionBorder}`,
        margin: "20px 0 16px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.07em", background: c.bgCard, padding: "0 8px", marginTop: -1 }}>
          {esAlquiler ? tv("Precio alquiler") : tv("Precios y Honorarios")}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))", gap: 12, marginBottom: 12 }}>
        {puedeVerPrecioVenta && (
        <CampoPrecio
          label={esAlquiler ? "Precio alquiler" : "Precio venta"}
          name="precio_venta"
          form={form}
          cambiarDirecto={cambiarDirecto}
          err={hasError("precio_venta")}
          inputId="campo-precio-venta"
          unidad={esAlquiler ? "€/mes" : "€"}
          c={c}
          isDark={isDark}
          labelStyle={labelStyle}
          inp={inp}
          disabled={!puedeModificarPrecioVenta}
          tv={tv}
        />
        )}
        {puedeVerPrecioPropietario && (
        <CampoPrecio
          label="Precio propietario"
          name="precio_propietario"
          form={form}
          cambiarDirecto={cambiarDirecto}
          decimales={!esAlquiler}
          c={c}
          isDark={isDark}
          labelStyle={labelStyle}
          inp={inp}
          disabled={!puedeModificarPrecioPropietario}
          tv={tv}
        />
        )}
        {!esAlquiler && puedeVerHonorariosPct && (
        <CampoPrecio
          label="Honorarios %"
          name="honorarios_porcentaje"
          form={form}
          cambiarDirecto={cambiarDirecto}
          unidad="%"
          decimales
          c={c}
          isDark={isDark}
          labelStyle={labelStyle}
          inp={inp}
          disabled={!puedeModificarHonorariosPct}
          tv={tv}
        />
        )}
        {/* Alquiler: honorarios a mano (sin %/IVA/cálculo). Venta: € calculados. */}
        {esAlquiler && (puedeVerHonorariosEur || puedeVerHonorariosCalc) && (
        <CampoPrecio
          label="Honorarios"
          name="total_honorarios"
          form={form}
          cambiarDirecto={(name, val) => {
            cambiarDirecto(name, val);
            // Mantener gemelo honorarios_euros para sync/historico coherente.
            cambiarDirecto("honorarios_euros", val);
          }}
          decimales={false}
          c={c}
          isDark={isDark}
          labelStyle={labelStyle}
          inp={inp}
          disabled={!puedeModificarHonorariosEur && !puedeModificarHonorariosPct}
          tv={tv}
        />
        )}
        {!esAlquiler && puedeVerHonorariosEur && (
        <CampoPrecio
          label="Honorarios €"
          name="honorarios_euros"
          form={form}
          cambiarDirecto={cambiarDirecto}
          readOnly
          decimales
          c={c}
          isDark={isDark}
          labelStyle={labelStyle}
          inp={inp}
          disabled={!puedeModificarHonorariosEur}
          tv={tv}
        />
        )}
        {!esAlquiler && puedeVerIva && (
        <div>
          <label style={labelStyle}>{tv("IVA")}</label>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="number"
                step="0.01"
                name="iva_porcentaje"
                value={form.iva_porcentaje ?? 21}
                onChange={(e) => cambiarDirecto("iva_porcentaje", e.target.value)}
                disabled={!puedeModificarIva}
                title={!puedeModificarIva ? "No tienes permiso para modificar" : undefined}
                style={{ ...inp(false, false, false), fontSize: 13, opacity: puedeModificarIva ? 1 : 0.6, cursor: puedeModificarIva ? "text" : "not-allowed" }}
              />
              <span style={{ fontSize: 13, color: c.textMuted, fontWeight: 600 }}>%</span>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="text"
                value={formatEuroAmount(form.iva_euros)}
                readOnly
                style={{ ...inp(false, true, false), fontSize: 13 }}
              />
              <span style={{ fontSize: 13, color: c.textMuted, fontWeight: 600 }}>€</span>
            </div>
          </div>
        </div>
        )}
        {!esAlquiler && (
        <CampoPrecio
          label="Total honorarios"
          name="total_honorarios"
          form={form}
          cambiarDirecto={cambiarDirecto}
          readOnly
          highlight
          decimales
          c={c}
          isDark={isDark}
          labelStyle={labelStyle}
          inp={inp}
          tv={tv}
        />
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>{tv("Observaciones precio")}</label>
        <textarea
          name="precio_observaciones"
          rows={2}
          value={form.precio_observaciones ?? ""}
          onChange={cambiar}
          placeholder={tv("Notas sobre precio, negociación, condiciones...")}
          style={{
            ...inp(false, false, false),
            width: "100%",
            minHeight: 56,
            resize: "vertical",
            lineHeight: 1.45,
            fontFamily: "inherit",
            fontSize: 13,
          }}
        />
      </div>

      {!esAlquiler && puedeVerHonorariosCalc && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button type="button" onClick={calcDesdeVentaYProp}
          style={{
            padding: "7px 14px",
            background: isDark ? "#0f1f16" : "#f0fdf4",
            border: `1px solid ${c.accentGreen}`, color: c.accentGreen,
            borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
          {tv("↕ Calcular desde P.Venta − P.Propietario")}
        </button>
        <button type="button" onClick={calcDesdeVentaYPct}
          style={{
            padding: "7px 14px",
            background: isDark ? "#0f1f16" : "#f0fdf4",
            border: `1px solid ${c.accentGreen}`, color: c.accentGreen,
            borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
          {tv("% Calcular desde P.Venta + % Honorarios")}
        </button>
      </div>
      )}
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function SeccionDatosInternos(props) {
  const {
    form, cambiar, cambiarDirecto, camposError, auxiliares = {},
    propiedadId = null, historicoRefreshKey = 0, focusPrecioTick = 0, focusLlavesTick = 0,
  } = props;
  const { tv } = usePropiedadUi(props);
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeVerInternosMostrar = puede("internos_mostrar", "ver");
  const puedeVerPrecioVenta = puede("precio_venta", "ver");
  const puedeModificarPrecioVenta = puede("precio_venta", "modificar");
  const puedeVerPrecioPropietario = puede("precio_propietario", "ver");
  const puedeModificarPrecioPropietario = puede("precio_propietario", "modificar");
  const puedeVerHonorariosPct = puede("honorarios_pct", "ver");
  const puedeModificarHonorariosPct = puede("honorarios_pct", "modificar");
  const puedeVerHonorariosEur = puede("honorarios_eur", "ver");
  const puedeModificarHonorariosEur = puede("honorarios_eur", "modificar");
  const puedeVerHonorariosCalc = puede("honorarios_calc", "ver");
  const puedeVerIva = puede("iva", "ver");
  const puedeModificarIva = puede("iva", "modificar");
  const puedeVerAgenteCaptador = puede("agente_captador", "ver");
  const puedeModificarAgenteCaptador = puede("agente_captador", "modificar");
  const puedeVerProcedencia = puede("procedencia", "ver");
  const puedeModificarProcedencia = puede("procedencia", "modificar");
  const puedeVerCartel = puede("cartel", "ver");
  const puedeModificarCartel = puede("cartel", "modificar");
  const puedeVerPrecioNegociable = puede("precio_negociable", "ver");
  const puedeModificarPrecioNegociable = puede("precio_negociable", "modificar");
  const puedeVerLlaves = puede("llaves", "ver");
  const puedeModificarLlaves = puede("llaves", "modificar");
  const puedeVerHistoricoPrecios = puede("historico_precios", "ver");
  const esAlquiler = esOperacionAlquiler(form.operacion);

  const [agentes, setAgentes] = useState([]);
  const procedencias = auxiliares.procedencias || [];
  const [visible, setVisible] = useState(false);
  const pendingPrecioScrollRef = useRef(false);
  const pendingLlavesScrollRef = useRef(false);

  useEffect(() => {
    api.get("/agentes").then(res => setAgentes(res.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!focusPrecioTick) return;
    pendingPrecioScrollRef.current = true;
    setVisible(true);
  }, [focusPrecioTick]);

  useEffect(() => {
    if (!focusLlavesTick) return;
    pendingLlavesScrollRef.current = true;
    setVisible(true);
  }, [focusLlavesTick]);

  useEffect(() => {
    if (!visible || !pendingPrecioScrollRef.current) return;
    pendingPrecioScrollRef.current = false;
    const scrollToPrecio = () => {
      const anchor = document.getElementById("bloque-precios-venta");
      const input = document.getElementById("campo-precio-venta");
      const target = anchor || input;
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        input?.focus({ preventScroll: true });
      } else {
        document.getElementById("seccion-internos")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    const t = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToPrecio);
    });
    return () => window.cancelAnimationFrame(t);
  }, [visible, focusPrecioTick]);

  useEffect(() => {
    if (!visible || !pendingLlavesScrollRef.current) return;
    const scrollToLlaves = () => {
      const bloque = document.getElementById("bloque-llaves-ubicacion");
      const campo = document.getElementById("campo-tenemos-llaves");
      const target = bloque || campo;
      if (!target) return false;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      return true;
    };
    // Tras desplegar Internos el bloque «Han vuelto» puede montarse un frame después.
    const t = window.setTimeout(() => {
      if (scrollToLlaves()) {
        pendingLlavesScrollRef.current = false;
        return;
      }
      document.getElementById("seccion-internos")?.scrollIntoView({ behavior: "smooth", block: "start" });
      // Si aún no hay bloque (tenemos_llaves llega luego), se reintenta al cambiar form.
    }, 120);
    return () => window.clearTimeout(t);
  }, [visible, focusLlavesTick, form.tenemos_llaves]);

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 6, display: "block",
  };
  const inputStyle = {
    padding: "8px 12px", border: `1px solid ${c.chipBorder}`,
    borderRadius: 8, fontSize: 13, color: c.textPrimary,
    background: isDark ? '#0a0e16' : '#dceeff', width: "100%", outline: "none",
    boxShadow: isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)',
  };
  const selectStyle = { ...inputStyle, cursor: "pointer" };
  const textareaStyle = {
    ...inputStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
  };

  const chipStyle = (activo, disabled = false) => ({
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${activo ? c.chipActiveBorder : c.chipBorder}`,
    background: activo ? c.chipActiveBg : c.chipBg,
    color: activo ? c.chipActiveText : c.chipText,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "all 0.15s ease",
    userSelect: "none",
    lineHeight: "1.4",
  });
  const onChipEnter = (e, activo) => {
    if (!activo) {
      e.currentTarget.style.background = c.chipHoverBg;
      e.currentTarget.style.color = c.chipHoverText;
      e.currentTarget.style.borderColor = c.chipHoverBorder;
    }
  };
  const onChipLeave = (e, activo) => {
    if (!activo) {
      e.currentTarget.style.background = c.chipBg;
      e.currentTarget.style.color = c.chipText;
      e.currentTarget.style.borderColor = c.chipBorder;
    }
  };

  const onPrecioNegociableClick = (opt) => {
    if (!puedeModificarPrecioNegociable) return;
    const activo = String(form.precio_negociable || "") === opt.v;
    const nuevo = activo ? "" : opt.v;
    cambiarDirecto("precio_negociable", nuevo);
    cambiarDirecto(
      "info_visita",
      infoVisitaConPrecioNegociable(form.info_visita, nuevo, form.precio_negociable_obs)
    );
  };

  const onPrecioNegociableObsChange = (e) => {
    if (!puedeModificarPrecioNegociable) return;
    const obs = e.target.value;
    cambiarDirecto("precio_negociable_obs", obs);
    if (form.precio_negociable) {
      cambiarDirecto(
        "info_visita",
        infoVisitaConPrecioNegociable(form.info_visita, form.precio_negociable, obs)
      );
    }
  };

  const cartelSel = Array.isArray(form.cartel_opciones) ? form.cartel_opciones : [];
  const toggleCartel = (opt) => {
    if (!puedeModificarCartel) return;
    const set = new Set(cartelSel.map(String));
    if (set.has(opt)) set.delete(opt);
    else set.add(opt);
    cambiarDirecto("cartel_opciones", [...set]);
  };

  return (
    <div>
      {/* Botón mostrar/ocultar — oculto por defecto */}
      {puedeVerInternosMostrar && (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" onClick={() => setVisible(v => !v)}
          style={{
            fontSize: 12, fontWeight: 600,
            color: c.accentBlue,
            background: "none", border: "none",
            cursor: "pointer", padding: 0,
          }}>
          {visible ? tv("Ocultar") : tv("Mostrar datos internos")}
        </button>
      </div>
      )}

      {visible && puedeVerInternosMostrar && (
        <div style={{ marginTop: 16 }}>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, minWidth: 0 }}>
            {puedeVerAgenteCaptador && (
            <div>
              <label style={labelStyle}>{tv("Agente responsable")}</label>
              <select
                name="agente_id"
                value={form.agente_id || ""}
                onChange={cambiar}
                disabled={!puedeModificarAgenteCaptador}
                title={!puedeModificarAgenteCaptador ? "No tienes permiso para modificar" : undefined}
                style={{ ...selectStyle, opacity: puedeModificarAgenteCaptador ? 1 : 0.6, cursor: puedeModificarAgenteCaptador ? "pointer" : "not-allowed" }}
              >
                <option value="">{tv("Sin asignar")}</option>
                {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </div>
            )}
            {puedeVerProcedencia && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>{tv("Procedencia")}</label>
              {procedencias.length === 0 ? (
                <span style={{ fontSize: 12, color: c.textMuted }}>
                  {tv("Configura procedencias en Tablas auxiliares.")}
                </span>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {procedencias.map((p) => {
                    const activo = String(form.procedencia_id || "") === String(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => puedeModificarProcedencia && cambiarDirecto("procedencia_id", activo ? "" : p.id)}
                        disabled={!puedeModificarProcedencia}
                        title={!puedeModificarProcedencia ? "No tienes permiso para modificar" : undefined}
                        style={chipStyle(activo, !puedeModificarProcedencia)}
                        onMouseEnter={(e) => onChipEnter(e, activo)}
                        onMouseLeave={(e) => onChipLeave(e, activo)}
                      >
                        {emojiVisualProcedencia(p.nombre, p.emoji)} {p.nombre}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}
            <div>
              <label style={labelStyle}>{tv("Alias interno")}</label>
              <input type="text" name="alias" value={form.alias || ""} onChange={cambiar}
                placeholder={tv("Nombre corto interno")} style={inputStyle} />
            </div>
            {puedeVerCartel && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>{tv("Cartel (puede elegir varias)")}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {CARTEL_OPCIONES.map((opt) => {
                  const activo = cartelSel.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleCartel(opt)}
                      disabled={!puedeModificarCartel}
                      title={!puedeModificarCartel ? "No tienes permiso para modificar" : undefined}
                      style={chipStyle(activo, !puedeModificarCartel)}
                      onMouseEnter={(e) => onChipEnter(e, activo)}
                      onMouseLeave={(e) => onChipLeave(e, activo)}
                    >
                      {tv(opt)}
                    </button>
                  );
                })}
              </div>
            </div>
            )}
            {puedeVerLlaves && (
            <div id="campo-tenemos-llaves" style={{ scrollMarginTop: 120 }}>
              <label style={labelStyle}>{tv("Llaves")}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  onClick={() => puedeModificarLlaves && cambiarDirecto('tenemos_llaves', !form.tenemos_llaves)}
                  title={!puedeModificarLlaves ? "No tienes permiso para modificar" : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, cursor: puedeModificarLlaves ? 'pointer' : 'not-allowed',
                    opacity: puedeModificarLlaves ? 1 : 0.6,
                    padding: '8px 12px', borderRadius: 8,
                    border: `1px solid ${form.tenemos_llaves ? c.accentBlue : c.chipBorder}`,
                    background: isDark ? '#0a0e16' : '#dceeff',
                    boxShadow: isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5)' : 'inset 0 3px 6px rgba(100,140,200,0.18)',
                    userSelect: 'none',
                  }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${form.tenemos_llaves ? c.accentBlue : c.textMuted}`,
                    background: form.tenemos_llaves ? c.accentBlue : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {form.tenemos_llaves && (
                      <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: c.textPrimary, fontWeight: 500, whiteSpace: 'nowrap' }}>{tv("🔑 Tenemos llaves")}</span>
                </div>
                <input
                  type="text"
                  name="num_llave"
                  value={form.num_llave || ""}
                  onChange={cambiar}
                  placeholder={tv("Nº llave")}
                  disabled={!puedeModificarLlaves}
                  title={!puedeModificarLlaves ? "No tienes permiso para modificar" : undefined}
                  style={{ ...inputStyle, width: 90, opacity: puedeModificarLlaves ? 1 : 0.6, cursor: puedeModificarLlaves ? "text" : "not-allowed" }}
                />
              </div>
              {form.tenemos_llaves && (
                <div id="bloque-llaves-ubicacion" style={{ marginTop: 8, scrollMarginTop: 120, maxWidth: 420 }}>
                  <BloqueLlavesUbicacion
                    propiedadId={propiedadId}
                    referencia={form.referencia}
                    tenemosLlaves={!!form.tenemos_llaves}
                    theme={theme}
                    puedeModificar={puedeModificarLlaves}
                  />
                </div>
              )}
            </div>
            )}
            <div>
              <label style={labelStyle}>{tv("Calificación")}</label>
              <select
                name="calificacion"
                value={form.calificacion === 0 || form.calificacion === "0" ? "0" : (form.calificacion ?? "")}
                onChange={cambiar}
                style={selectStyle}
              >
                <option value="">{tv("Seleccionar")}</option>
                {CALIFICACIONES.map(cal => (
                  <option key={cal.v} value={String(cal.v)}>{tv(cal.label)}</option>
                ))}
              </select>
            </div>
            {puedeVerPrecioNegociable && (
            <div>
              <label style={labelStyle}>{tv("Precio negociable")}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {PRECIO_NEGOCIABLE_OPTS.map((opt) => {
                  const activo = String(form.precio_negociable || "") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => onPrecioNegociableClick(opt)}
                      disabled={!puedeModificarPrecioNegociable}
                      title={!puedeModificarPrecioNegociable ? "No tienes permiso para modificar" : undefined}
                      style={chipStyle(activo, !puedeModificarPrecioNegociable)}
                      onMouseEnter={(e) => onChipEnter(e, activo)}
                      onMouseLeave={(e) => onChipLeave(e, activo)}
                    >
                      {tv(opt.label)}
                    </button>
                  );
                })}
                <input
                  type="text"
                  name="precio_negociable_obs"
                  value={form.precio_negociable_obs || ""}
                  onChange={onPrecioNegociableObsChange}
                  disabled={!puedeModificarPrecioNegociable}
                  title={!puedeModificarPrecioNegociable ? "No tienes permiso para modificar" : undefined}
                  placeholder={tv("Obs.")}
                  style={{
                    ...inputStyle,
                    flex: "1 1 140px",
                    minWidth: 120,
                    fontSize: 12,
                    padding: "6px 10px",
                    opacity: puedeModificarPrecioNegociable ? 1 : 0.6,
                    cursor: puedeModificarPrecioNegociable ? "text" : "not-allowed",
                  }}
                />
              </div>
            </div>
            )}
            {puedeVerLlaves && (
            <div>
              <label style={labelStyle}>{tv("Recoger llaves en")}</label>
              <input
                type="text"
                name="recoger_llaves_en"
                value={form.recoger_llaves_en || ""}
                onChange={cambiar}
                disabled={!puedeModificarLlaves}
                title={!puedeModificarLlaves ? "No tienes permiso para modificar" : undefined}
                style={{ ...inputStyle, opacity: puedeModificarLlaves ? 1 : 0.6, cursor: puedeModificarLlaves ? "text" : "not-allowed" }}
              />
            </div>
            )}
            <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <label style={labelStyle}>{tv("Info visita")}</label>
              <textarea name="info_visita" value={form.info_visita || ""} onChange={cambiar} rows={3}
                placeholder={tv("Instrucciones para la visita, acceso, códigos...")}
                style={{ ...textareaStyle, maxWidth: "100%", boxSizing: "border-box" }} />
            </div>
            <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <label style={labelStyle}>{tv("Observaciones internas")}</label>
              <textarea name="observaciones_internas" value={form.observaciones_internas || ""} onChange={cambiar} rows={3}
                placeholder={tv("Notas solo para el equipo (no se publican en la web)...")}
                style={{ ...textareaStyle, maxWidth: "100%", boxSizing: "border-box" }} />
            </div>
          </div>

          <BloquePrecios
            form={form}
            cambiar={cambiar}
            cambiarDirecto={cambiarDirecto}
            camposError={camposError}
            c={c}
            isDark={isDark}
            puedeVerPrecioVenta={puedeVerPrecioVenta}
            puedeModificarPrecioVenta={puedeModificarPrecioVenta}
            puedeVerPrecioPropietario={puedeVerPrecioPropietario}
            puedeModificarPrecioPropietario={puedeModificarPrecioPropietario}
            puedeVerHonorariosPct={puedeVerHonorariosPct}
            puedeModificarHonorariosPct={puedeModificarHonorariosPct}
            puedeVerHonorariosEur={puedeVerHonorariosEur}
            puedeModificarHonorariosEur={puedeModificarHonorariosEur}
            puedeVerHonorariosCalc={puedeVerHonorariosCalc}
            puedeVerIva={puedeVerIva}
            puedeModificarIva={puedeModificarIva}
            esAlquiler={esAlquiler}
            tv={tv}
          />

          <BloqueHistoricoPrecios
            propiedadId={propiedadId || form.id || null}
            historicoRefreshKey={historicoRefreshKey}
            c={c}
            isDark={isDark}
            puedeVer={puedeVerHistoricoPrecios}
            esAlquiler={esAlquiler}
            tv={tv}
          />

        </div>
      )}
    </div>
  );
}
